import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPause, faPlay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { Button, Checkbox, ConfirmModalV1, toast } from '@feathr/components';
import { errorMessage, useToggle } from '@feathr/hooks';

type TButtonType = 'disconnect' | 'pause' | 'resume';

interface IProps {
  readonly integration: BlackbaudRaisersEdgeIntegration;
  readonly type: TButtonType;
}

function getIconFromType(type: TButtonType): IconProp {
  const map = {
    // Disconnect does not have an icon.
    pause: faPause,
    resume: faPlay,
  };

  return map[type];
}

function RaisersEdgeControlButton({ integration, type }: IProps): JSX.Element {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [disconnectAcknowledged, toggleDisconnectAcknowledged] = useToggle(false);

  function getErrorAction(): string {
    const map = {
      disconnect: t('disconnect'),
      pause: t('pause'),
      resume: t('resume'),
    };

    return map[type];
  }

  function getSuccessAction(): string {
    const map = {
      disconnect: t('disconnected'),
      pause: t('paused'),
      resume: t('resumed'),
    };
    return map[type];
  }

  const requests: Record<TButtonType, () => Promise<unknown>> = {
    disconnect: () => integration.disconnectIntegration(),
    pause: () => integration.pauseIntegration(),
    resume: () => integration.resumeIntegration(),
  };

  function openModal(): void {
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
  }

  async function handleConfirm(): Promise<void> {
    setIsPending(true);
    try {
      // The model applies the server's response, so the page re-renders without a reload.
      await requests[type]();

      toast(
        t('Your integration has been successfully {{action}}.', {
          action: getSuccessAction(),
        }),
        { type: 'success' },
      );
    } catch (error) {
      toast(
        t('Something went wrong while trying to {{action}} your integration: {{- error}}', {
          action: getErrorAction(),
          error: errorMessage(error, t),
        }),
        { type: 'error' },
      );
      return;
    } finally {
      setIsPending(false);
      closeModal();
    }

    // Pausing and resuming change the webhook subscriptions, which the mappings report on.
    if (type !== 'disconnect') {
      await integration.contactMappingStore.refresh();
    }
  }

  const buttonText = (function (): string {
    const map = { disconnect: t('Disconnect'), pause: t('Pause'), resume: t('Resume') };
    return map[type];
  })();

  const confirmButtonText = (function (): string {
    const map = {
      disconnect: t('Disconnect integration'),
      pause: t('Pause integration'),
      resume: t('Resume integration'),
    };
    return map[type];
  })();

  const title = (function (): string {
    const map = {
      disconnect: t('Disconnect integration?'),
      pause: t('Pause integration?'),
      resume: t('Resume integration?'),
    };
    return map[type];
  })();

  const description = (function (): string {
    const map = {
      disconnect: t(
        'Are you sure you want to disconnect this integration? Data will not sync while this integration is disconnected.',
      ),
      pause: t(
        'Are you sure you want to pause this integration? Data will not sync while this integration is paused.',
      ),
      resume: t(
        'Are you sure you want to resume this integration? Data will sync again once this integration is resumed.',
      ),
    };

    return map[type];
  })();

  const key = `${type}-raisers-edge-integration`;
  const isDisconnectButton = type === 'disconnect';

  return (
    <>
      <Button
        key={key}
        name={key}
        onClick={openModal}
        prefix={isDisconnectButton ? null : <FontAwesomeIcon icon={getIconFromType(type)} />}
      >
        {buttonText}
      </Button>
      {showModal && (
        <ConfirmModalV1
          cancelButtonText={t('Cancel')}
          confirmButtonText={confirmButtonText}
          confirmButtonType={isDisconnectButton ? 'danger' : undefined}
          confirmDisabled={isPending || (isDisconnectButton && !disconnectAcknowledged)}
          description={description}
          onClose={closeModal}
          onConfirm={handleConfirm}
          t={t}
          title={title}
        >
          {isDisconnectButton && (
            <Checkbox
              label={t('Yes, I want to disconnect this integration')}
              onChange={toggleDisconnectAcknowledged}
            />
          )}
        </ConfirmModalV1>
      )}
    </>
  );
}

export default observer(RaisersEdgeControlButton);
